import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaPlus, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { AppContext } from '../../App';

const ServicesManager = () => {
  const { services, setServices } = useContext(AppContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '📈',
    features: [],
    benefits: [],
    type: 'financial-planning',
    isActive: true
  });
  const [featureInput, setFeatureInput] = useState('');
  const [benefitInput, setBenefitInput] = useState('');

  const iconOptions = [
    '📈', '🏥', '🛡️', '👴', '🏠', '🚗', '💰', '📋', '🎯', '💼', '📊', '🔒'
  ];

  const typeOptions = [
    { value: 'mutual-fund', label: 'Mutual Fund' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'loan', label: 'Loan' },
    { value: 'financial-planning', label: 'Financial Planning' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    if (editingService) {
      const updatedServices = services.map(s =>
        s.id === editingService.id ? { ...formData, id: s.id } : s
      );
      setServices(updatedServices);
      toast.success('Service updated successfully!');
    } else {
      const newService = {
        ...formData,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      setServices([...services, newService]);
      toast.success('Service added successfully!');
    }

    resetForm();
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      setServices(services.filter(s => s.id !== id));
      toast.success('Service deleted successfully!');
    }
  };

  const handleToggleStatus = (id) => {
    const updatedServices = services.map(s =>
      s.id === id ? { ...s, isActive: !s.isActive } : s
    );
    setServices(updatedServices);
    toast.success('Service status updated!');
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      icon: service.icon,
      features: service.features || [],
      benefits: service.benefits || [],
      type: service.type,
      isActive: service.isActive
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      title: '',
      description: '',
      icon: '📈',
      features: [],
      benefits: [],
      type: 'financial-planning',
      isActive: true
    });
    setFeatureInput('');
    setBenefitInput('');
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({ ...formData, features: [...formData.features, featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const addBenefit = () => {
    if (benefitInput.trim()) {
      setFormData({ ...formData, benefits: [...formData.benefits, benefitInput.trim()] });
      setBenefitInput('');
    }
  };

  const removeBenefit = (index) => {
    setFormData({
      ...formData,
      benefits: formData.benefits.filter((_, i) => i !== index)
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-primary">Manage Services</h2>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <FaPlus /> Add New Service
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {services.map((service) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md overflow-hidden"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{service.icon}</span>
                  <div>
                    <h3 className="font-bold text-xl text-primary">{service.title}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${service.type === 'mutual-fund' ? 'bg-blue-100 text-blue-700' :
                        service.type === 'insurance' ? 'bg-green-100 text-green-700' :
                          service.type === 'loan' ? 'bg-purple-100 text-purple-700' :
                            'bg-orange-100 text-orange-700'
                      }`}>
                      {typeOptions.find(t => t.value === service.type)?.label}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleStatus(service.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title={service.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {service.isActive ? <FaEye className="text-green-500" /> : <FaEyeSlash className="text-gray-400" />}
                  </button>
                  <button
                    onClick={() => handleEdit(service)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <FaEdit className="text-blue-500" />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <FaTrash className="text-red-500" />
                  </button>
                </div>
              </div>
              <p className="text-gray-600 mb-4">{service.description}</p>
              {service.features && service.features.length > 0 && (
                <div className="mb-3">
                  <p className="font-semibold text-sm text-gray-700 mb-1">Features:</p>
                  <div className="flex flex-wrap gap-1">
                    {service.features.slice(0, 3).map((f, i) => (
                      <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">✓ {f}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className={`mt-3 pt-3 border-t text-sm ${service.isActive ? 'text-green-600' : 'text-red-600'}`}>
                Status: {service.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-gradient-to-r from-primary to-secondary text-white p-4 flex justify-between items-center">
                <h3 className="text-xl font-bold">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="hover:bg-white/20 rounded-full p-2">
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block font-semibold mb-1">Service Title *</label>
                  <input
                    type="text"
                    required
                    className="input-field"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-1">Description *</label>
                  <textarea
                    required
                    rows="3"
                    className="input-field"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-1">Icon</label>
                  <div className="flex flex-wrap gap-2">
                    {iconOptions.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className={`text-2xl p-2 rounded-lg transition-all ${formData.icon === icon ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block font-semibold mb-1">Service Type</label>
                  <select
                    className="input-field"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    {typeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-semibold mb-1">Features</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      className="input-field flex-1"
                      placeholder="Add a feature"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                    />
                    <button type="button" onClick={addFeature} className="btn-primary px-4">Add</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.features.map((feature, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                        {feature}
                        <button type="button" onClick={() => removeFeature(idx)} className="hover:text-red-600">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block font-semibold mb-1">Benefits</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      className="input-field flex-1"
                      placeholder="Add a benefit"
                      value={benefitInput}
                      onChange={(e) => setBenefitInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addBenefit()}
                    />
                    <button type="button" onClick={addBenefit} className="btn-primary px-4">Add</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.benefits.map((benefit, idx) => (
                      <span key={idx} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                        {benefit}
                        <button type="button" onClick={() => removeBenefit(idx)} className="hover:text-red-600">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="font-semibold">Active (visible on website)</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {editingService ? 'Update Service' : 'Add Service'}
                  </button>
                  <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 btn-outline">
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServicesManager;